import { useTheme } from "@mui/material";
import { BarSeriesOption, EChartsOption, TooltipComponentOption } from "echarts";
import ReactECharts from "echarts-for-react";

type BaseChartProps = {
  axisLabels: string[];
  overrides?: Partial<EChartsOption>;
};

type HorizontalBarChartProps = BaseChartProps & {
  series: BarSeriesOption[];
  xAxisLabelFormatter?: (value: number) => string;
  yAxisLabelFormatter?: (value: string) => string;
  tooltipOptions?: TooltipComponentOption;
  stacked?: boolean;
  hideLegend?: boolean;
};

export const HorizontalBarChart = ({
  axisLabels,
  series,
  xAxisLabelFormatter,
  yAxisLabelFormatter,
  tooltipOptions,
  stacked = false,
  overrides = {},
  hideLegend = false,
}: HorizontalBarChartProps) => {
  const theme = useTheme();

  const baseOption: EChartsOption = {
    grid: {
      top: 40,
      left: 140,
    },
    tooltip: {
      trigger: "axis",
      padding: 0,
      borderWidth: 0,
      borderColor: "transparent",
      axisPointer: {
        type: "shadow",
      },
      backgroundColor: theme.palette.echartTooltip.background,
      textStyle: {
        color: theme.palette.echartTooltip.textColor,
      },
      ...(tooltipOptions ?? {}),
    },
    yAxis: {
      data: axisLabels,
      axisLabel: {
        color: theme.palette.brandedTypography?.textColor,
        fontSize: 14,
        fontWeight: 600,
        interval: 0,
        formatter: yAxisLabelFormatter,
      },
    },
    xAxis: {
      type: "value",
      axisLabel: {
        fontSize: 14,
        color: theme.palette.brandedTypography?.textColor,
        formatter: xAxisLabelFormatter,
      },
      splitLine: {
        show: true,
        lineStyle: {
          type: "dashed",
          color: theme.palette.ChartGuideLines.color,
        },
      },
    },
    series: series.map((data) => ({
      ...data,
      type: "bar",
      color: data.color,
      barWidth: data.barWidth ?? 12,
      barMinHeight: 6,
      ...(stacked && {
        stack: "total",
      }),
    })),
    ...(!hideLegend && {
      legend: {
        show: true,
        bottom: 0,
        itemGap: 20,
        itemWidth: 12,
        itemHeight: 12,
        borderRadius: 10,
        textStyle: {
          color: theme.palette.brandedTypography?.textColor,
          fontSize: 14,
          fontWeight: 600,
          fontFamily: "Montserrat, sans-serif",
          padding: [0, 0, 0, 4],
        },
      },
    }),
  };

  const option: EChartsOption = {
    ...baseOption,
    ...overrides,
  };

  return (
    <ReactECharts
      option={option}
      style={{ height: axisLabels.length * 60, minHeight: 300, maxHeight: 620 }}
      notMerge={true}
    />
  );
};

export default HorizontalBarChart;
